import { z } from "zod";
import type { LaunchMetadata } from "../../../wallet/launchpad-transactions";
import type { Address } from "viem";
import { LaunchApiError, envelope, launchRequest, metadataDraftDto, metadataDraftHandle, tokenDetail, tokenDto, type MetadataDraftHandle } from "../../../api/launchpad";
import { descriptionBytes, descriptionLimit } from "./draft";

export async function waitForLaunchIndex(token: Address, account: Address, block: number, hash: string, signal: AbortSignal) {
  while (!signal.aborted) {
    try {
      const detail = await launchRequest("/tokens/" + token, tokenDetail(token), { signal });
      if (detail.meta.indexedBlock >= block) {
        if (detail.data.creator.toLowerCase() !== account.toLowerCase() || detail.data.createdBlock !== block ||
            detail.data.createdTx.toLowerCase() !== hash.toLowerCase()) throw new Error("Indexed token does not match the launch receipt.");
        return;
      }
    } catch (error) {
      signal.throwIfAborted();
      if (!(error instanceof TypeError) && !(error instanceof LaunchApiError && error.status === 404 && error.message === "Token not found")) throw error;
    }
    await new Promise(resolve => setTimeout(resolve, 1500));
  }
  signal.throwIfAborted();
}

export async function hashLaunchLogo(file: File) {
  return Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", await file.arrayBuffer())),
    byte => byte.toString(16).padStart(2, "0")).join("");
}

export async function uploadMetadataDraft(account: Address, fields: LaunchMetadata["fields"], logo: File, signal?: AbortSignal) {
  fields = { ...fields, description: fields.description.trim() };
  if (descriptionBytes(fields.description) > descriptionLimit) throw new Error("Use at most 2,000 UTF-8 bytes.");
  if (logo.size >= 1_000_000) throw new Error("Choose a logo smaller than 1 MB.");
  const form = new FormData();
  form.set("creator", account);
  Object.entries(fields).forEach(([key, value]) => form.set(key, value));
  form.set("logo", logo, logo.name);
  const result = await launchRequest("/metadata-drafts", envelope(metadataDraftDto.extend({ attachKey: z.string().min(1) })),
    { method: "POST", body: form, signal: signal ?? null });
  const data = result.data;
  if (data.creator.toLowerCase() !== account.toLowerCase() || data.status !== "pending" || data.attachedToken !== null ||
      (data.description ?? "").trim() !== fields.description ||
      (["website", "telegram", "x"] as const).some(key =>
        (data[key] ?? "") !== fields[key])) {
    throw new Error("Metadata draft does not match the submitted fields.");
  }
  return metadataDraftHandle.parse(data);
}

export async function readMetadataDraft(draft: MetadataDraftHandle, signal?: AbortSignal) {
  const { data } = await launchRequest("/metadata-drafts/" + encodeURIComponent(draft.draftId), envelope(metadataDraftDto), { signal: signal ?? null });
  if (data.draftId !== draft.draftId || data.creator.toLowerCase() !== draft.creator.toLowerCase()) {
    throw new Error("Metadata draft identity mismatch.");
  }
  return data;
}

export async function attachMetadataDraft(draft: MetadataDraftHandle, token: Address | undefined, hash: string, block: number,
  signal: AbortSignal, check: () => void, beforeAttach: () => void) {
  check();
  beforeAttach();
  while (!signal.aborted) {
    check();
    try {
      const result = await launchRequest("/metadata-drafts/" + encodeURIComponent(draft.draftId) + "/attach",
        envelope(tokenDto).extend({ draft: metadataDraftDto }), {
          method: "POST", signal: AbortSignal.any([signal, AbortSignal.timeout(15000)]), headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ txHash: hash, attachKey: draft.attachKey }),
        });
      check();
      if ((token && result.data.address.toLowerCase() !== token.toLowerCase()) || result.draft.draftId !== draft.draftId || result.draft.status !== "attached" ||
          result.draft.creator.toLowerCase() !== draft.creator.toLowerCase() ||
          result.draft.attachedToken?.toLowerCase() !== result.data.address.toLowerCase() ||
          result.data.creator.toLowerCase() !== draft.creator.toLowerCase() ||
          result.data.createdTx.toLowerCase() !== hash.toLowerCase() || result.data.createdBlock !== block) {
        throw new Error("Attached metadata does not match this launch.");
      }
      return result.data;
    } catch (error) {
      signal.throwIfAborted();
      if (!(error instanceof LaunchApiError && error.status === 404 && error.retryable)) throw error;
    }
    await new Promise(resolve => setTimeout(resolve, 1500));
  }
  signal.throwIfAborted();
  throw new Error("Metadata attach interrupted.");
}
